// packages
import { cloneElement, Component, h } from 'preact';

// internal
import debounce from '../utils/debounce';
import noop from '../utils/noop';

class Typeahead extends Component {
  constructor(props) {
    super(props);

    this.state = {
      highlightedIndex: null,
      isOpen: false,
    };

    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handleOnClear = this.handleOnClear.bind(this);
    this.handleOnFocus = this.handleOnFocus.bind(this);
    this.handleOnInput = this.handleOnInput.bind(this);
    this.debouncedHandleOnInput = debounce(
      this.handleOnInput,
      this.props.debounceDuration
    );
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.setInputRef = this.setInputRef.bind(this);
    this.setMenuRef = this.setMenuRef.bind(this);
    this.setItemRef = this.setItemRef.bind(this);

    this.listRefs = {};
  }

  static getDerivedStateFromProps(_, prevState) {
    if (prevState.highlightedIndex == null) {
      return { highlightedIndex: 0 };
    }
  }

  componentDidUpdate() {
    const { highlightedIndex } = this.state;
    const count = this.getFilteredItems(this.props).length;

    if (Boolean(highlightedIndex) && highlightedIndex >= count) {
      this.setState({ highlightedIndex: 0 });
    }

    this.maybeScrollItemIntoView();
  }

  setInputRef(element) {
    this.inputRef = element;
  }

  setMenuRef(element) {
    this.menuRef = element;
  }

  setItemRef(element, idx) {
    this.listRefs[`item-${idx}`] = element;
  }

  maybeScrollItemIntoView() {
    const { highlightedIndex, isOpen } = this.state;

    if (isOpen && highlightedIndex != null) {
      const itemNode = this.listRefs[`item-${this.state.highlightedIndex}`];
      const menuNode = this.menuRef;

      if (itemNode && menuNode) {
        itemNode.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  getFilteredItems(props) {
    let { items } = props;

    const { shouldItemRender, sortItems } = props;

    if (shouldItemRender) {
      items = items.filter(item => shouldItemRender(item, props.value));
    }

    if (sortItems) {
      items = items.sort((a, b) => sortItems(a, b, props.value));
    }

    return items;
  }

  highlightItemOnMouseEnter(highlightedIndex) {
    this.setState({ highlightedIndex });
  }

  selectItemOnClick(item) {
    const { getItemValue, onSelect } = this.props;

    const value = getItemValue(item);

    this.setState(
      {
        highlightedIndex: null,
        isOpen: false,
      },
      () => {
        this.inputRef.blur();
        onSelect(value, item);
      }
    );
  }

  renderList() {
    const { highlightedIndex } = this.state;
    const props = this.props;

    if (!props.value) return null;

    const items = this.getFilteredItems(props);

    return (
      <ul
        class="listbox"
        ref={this.setMenuRef}
        onMouseDown={event => event.preventDefault()}
      >
        {items.map((item, idx) => {
          return cloneElement(
            props.renderItem(item, idx === highlightedIndex),
            {
              onClick: () => this.selectItemOnClick(item),
              onMouseEnter: () => this.highlightItemOnMouseEnter(idx),
              ref: el => this.setItemRef(el, idx),
            }
          );
        })}
        {items.length > 0 && props.renderAttribution()}
      </ul>
    );
  }

  handleOnClear(event) {
    this.setState({ isOpen: false });

    this.props.onClear(event);
  }

  handleOnBlur() {
    this.setState({ isOpen: false });

    const { onBlur } = this.props.inputProps;
    if (onBlur) onBlur(event);
  }

  handleOnFocus(event) {
    const { value } = this.props;

    if (value.length > 0) this.setState({ isOpen: true });

    const { onFocus } = this.props.inputProps;
    if (onFocus) onFocus(event);
  }

  handleOnInput(event) {
    const { value } = event.target;

    this.props.onChange(value, event);

    if (value.length <= 0) {
      this.setState({ highlightedIndex: null });
    }
  }

  handleKeyDown(event) {
    const { isOpen } = this.state;

    if (isOpen && Typeahead.keyEvents[event.key]) {
      Typeahead.keyEvents[event.key].call(this, event);
    } else if (!isOpen) {
      this.setState({ isOpen: true });
    }
  }

  render() {
    const { inputProps, onClear, renderInput, value } = this.props;
    const { isOpen } = this.state;

    return (
      <div class="typeahead">
        <div class="typeahead__input-wrapper">
          {renderInput({
            ...inputProps,
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': String(isOpen),
            autocomplete: 'off',
            onBlur: this.handleOnBlur,
            onFocus: this.handleOnFocus,
            onInput: this.handleOnInput,
            onKeyDown: this.handleKeyDown,
            value,
            ref: this.setInputRef,
          })}
          {onClear && value && (
            <button
              onClick={this.handleOnClear}
              type="button"
              class="close"
              aria-label="Clear"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          )}
        </div>
        {isOpen && this.renderList()}
      </div>
    );
  }
}

Typeahead.defaultProps = {
  debounceDuration: 150,
  inputProps: {},
  onChange: noop,
  onClear: noop,
  onSelect: noop,
  renderInput: props => <input {...props} />,
  value: '',
};

Typeahead.keyEvents = {
  ArrowDown(event) {
    event.preventDefault();

    const items = this.getFilteredItems(this.props);
    const itemsCount = items.length;
    if (itemsCount < 1) return;

    const { highlightedIndex } = this.state;

    const newIndex =
      highlightedIndex < itemsCount - 1
        ? highlightedIndex + 1
        : highlightedIndex;

    this.setState({ highlightedIndex: newIndex });
  },

  ArrowUp(event) {
    event.preventDefault();

    const items = this.getFilteredItems(this.props);
    const itemsCount = items.length;
    if (itemsCount < 1) return;

    const { highlightedIndex } = this.state;

    const newIndex =
      highlightedIndex > 0 ? highlightedIndex - 1 : highlightedIndex;

    this.setState({ highlightedIndex: newIndex });
  },

  Enter(event) {
    // not the right kind of Enter, abort
    if (event.keyCode !== 13) return;

    const { highlightedIndex, isOpen } = this.state;

    if (!isOpen) {
      // the menu is not open
      return;
    } else if (highlightedIndex == null) {
      // input has control, but there's nothing selected
      this.setState({ isOpen: false }, () => this.inputRef.select());
    } else {
      // the input has control and we have an item
      event.preventDefault();

      const item = this.getFilteredItems(this.props)[highlightedIndex];
      const value = this.props.getItemValue(item);

      this.setState(
        {
          highlightedIndex: null,
          isOpen: false,
        },
        () => {
          this.inputRef.setSelectionRange(value.length, value.length);
          this.props.onSelect(value, item);
        }
      );
    }
  },

  Escape() {
    this.setState({
      highlightedIndex: null,
      isOpen: false,
    });
  },
};

export default Typeahead;
